import React from 'react'
import { css } from '@emotion/core'
import { Row, Paragraph, colors, H2, WhiteTheme } from '@fs/zion-ui'
import { boolAttr } from '@fs/zion-frontend-friends'
import { useTranslation } from 'react-i18next'
import useFonts from '../../hooks/useFonts/useFonts'
import { useChineseSurnameInfo } from '../../providers/ChineseSurnameProvider'

const meaningContainerCss = css`
  display: flex;
  flex-direction: column;
  width: 35%;
  max-width: 300px;
  min-width: 120px;
  word-wrap: break-word;
`

const fullWidthMeaningContainerCss = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 300px;
  min-width: 100px;
  word-wrap: break-word;
`

const surnameCss = (surnameCircleColor) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: fit-content;
  aspect-ratio: 1 / 1;
  position: relative;
  transform: translateX(-50%);
  background-color: ${surnameCircleColor};
  color: ${colors.gray100};
  font-family: var(--font-family);
  font-size: clamp(20px, 25vw, 100px);
  inset-inline-start: 50%;
  justify-content: center;

  line-height: normal;
  border-radius: 50%;

  span {
    padding: 20px 20px 0;
  }

  small {
    display: block;
    font-size: 15px;
    letter-spacing: 0.08px;
    line-height: 24.8px;
    margin-top: 5px;
  }

  [data-two-chars] & {
    font-size: clamp(30px, 25vw, 55px);
    letter-spacing: 0.5px;
    @media print {
      font-size: 45px;
      span {
        padding: 15px 15px 0;
      }
    }
  }

  @media print {
    font-size: 80px;
    margin-inline-end: 30px;
  }
`

const surnameAndMeaningContainerCss = css`
  position: absolute;
  top: 0px;
  inset-inline-start: 8%;
  width: 100%;
  z-index: 1;
  height: 50%;
`
const fullWidthSurnameAndMeaningContainerCss = css`
  width: 100%;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ChineseSurnameHeader = ({ surname, romanization, meaning, isFullWidth, color, hasDarkBackground }) => {
  const { regularKaiti, isFontLoaded } = useFonts()
  const { colorValuesMap } = useChineseSurnameInfo()
  const { surnameCircleColor } = colorValuesMap[color]

  const [t] = useTranslation()

  // Don't render the header until the font is loaded to prevent layout jumping
  if (!isFontLoaded) {
    return null
  }

  return (
    <div css={isFullWidth ? fullWidthSurnameAndMeaningContainerCss : surnameAndMeaningContainerCss}>
      <Row alignY="middle" gutters="xxs" data-two-chars={boolAttr(surname?.length === 2)}>
        <h1
          css={surnameCss(surnameCircleColor)}
          style={{
            '--font-family': `${regularKaiti}, "Noto Sans CJK SC", "Source Han Sans SC", serif`,
          }}
        >
          <span>{surname}</span>
          <small>{romanization}</small>
        </h1>
        {hasDarkBackground ? (
          <div css={isFullWidth ? fullWidthMeaningContainerCss : meaningContainerCss}>
            <WhiteTheme>
              <H2 size="H5">
                <strong>{t('SurnamePage.surname.meaning', 'Meaning')}</strong>
              </H2>
              <Paragraph size={meaning?.length > 120 ? 'xs' : 'sm'} dense>
                {meaning}
              </Paragraph>
            </WhiteTheme>
          </div>
        ) : (
          <div css={isFullWidth ? fullWidthMeaningContainerCss : meaningContainerCss}>
            <H2 size="H5">
              <strong>{t('SurnamePage.surname.meaning', 'Meaning')}</strong>
            </H2>
            <Paragraph size={meaning?.length > 120 ? 'xs' : 'sm'} dense>
              {meaning}
            </Paragraph>
          </div>
        )}
      </Row>
    </div>
  )
}

export default ChineseSurnameHeader
